/**
 * Selettore del tema: chiaro · auto · scuro (F-070).
 *
 * Tre pulsanti e non un interruttore che cicla: con un solo bottone lo stato
 * corrente si deduce dall'icona e quello successivo non si sa, e `auto` — che
 * è l'impostazione predefinita — diventa una posizione in cui si capita per
 * sbaglio invece di una che si sceglie.
 *
 * Semanticamente è un gruppo di radio: le tre opzioni si escludono e la
 * tastiera deve poterle scorrere come una scelta sola, non come tre pulsanti
 * indipendenti.
 */

import { Icon, type IconName } from '@/ui';
import { useTheme, type ThemeChoice } from './theme-context';
import './theme-toggle.css';

const OPTIONS: { value: ThemeChoice; label: string; icon: IconName }[] = [
  { value: 'light', label: 'Tema chiaro', icon: 'themeLight' },
  { value: 'auto', label: 'Tema automatico, come il sistema', icon: 'themeAuto' },
  { value: 'dark', label: 'Tema scuro', icon: 'themeDark' },
];

export function ThemeToggle() {
  const { choice, resolved, setChoice } = useTheme();

  return (
    <div className="theme-toggle" role="radiogroup" aria-label="Tema dell'interfaccia">
      {OPTIONS.map((option) => {
        const selected = choice === option.value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={selected}
            // Solo l'opzione scelta resta raggiungibile con Tab: dentro un
            // gruppo di radio si entra una volta e si scorre con le frecce.
            tabIndex={selected ? 0 : -1}
            className={`theme-toggle__option ${selected ? 'theme-toggle__option--selected' : ''}`}
            title={option.label}
            onClick={() => setChoice(option.value)}
            onKeyDown={(event) => {
              const step = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1
                : event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1
                : 0;
              if (step === 0) return;
              event.preventDefault();
              const current = OPTIONS.findIndex((o) => o.value === choice);
              const index = (current + step + OPTIONS.length) % OPTIONS.length;
              const next = OPTIONS[index];
              if (!next) return;
              setChoice(next.value);
              // Il focus segue la selezione: in un gruppo di radio le frecce
              // spostano entrambi, altrimenti il focus resta su un'opzione che
              // non è più quella scelta.
              const buttons = event.currentTarget.parentElement?.querySelectorAll('button');
              buttons?.[index]?.focus();
            }}
          >
            <Icon name={option.icon} />
            <span className="visually-hidden">{option.label}</span>
          </button>
        );
      })}
      {/* Con `auto` selezionato l'icona non dice quale dei due temi sia in
          vigore: chi non vede lo schermo non ha altro modo di saperlo. */}
      <span className="visually-hidden" aria-live="polite">
        {choice === 'auto' ? `Tema automatico: al momento ${resolved === 'dark' ? 'scuro' : 'chiaro'}` : ''}
      </span>
    </div>
  );
}
